/**
 * Dashboard Page
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPositions } from '../redux/slices/portfolioSlice';
import { fetchOrderBook } from '../redux/slices/orderSlice';
import { fetchFunds } from '../redux/slices/portfolioSlice';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { positions, funds, loading, error } = useSelector((state) => state.portfolio);
  const { orderBook } = useSelector((state) => state.orders);
  const { userId } = useSelector((state) => state.auth);

  useEffect(() => {
    // Try to fetch data, but don't fail if APIs are not implemented yet
    dispatch(fetchPositions()).catch(() => {});
    dispatch(fetchOrderBook()).catch(() => {});
    dispatch(fetchFunds()).catch(() => {});
  }, [dispatch]);

  // Calculate P&L
  const totalPnL = positions?.reduce((sum, pos) => {
    const pnl = parseFloat(pos.realizedPnl || pos.dayPnL || pos.rpnl || 0) || 0;
    return sum + pnl;
  }, 0) || 0;
  const pendingOrders = orderBook?.filter(o => o.orderStatus === 'OPEN' || o.orderStatus === 'PENDING').length || 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
        <div className="text-sm text-gray-600">
          Welcome, User ID: <span className="font-semibold">{userId}</span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-600 text-white p-6 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-blue-300/20">
          <h3 className="text-sm font-medium text-blue-100 mb-2">Available Balance</h3>
          <p className="text-2xl font-bold text-white drop-shadow-lg">
            ₹{funds?.openingCashLimit?.toLocaleString('en-IN') || '0'}
          </p>
          <div className="mt-2 text-blue-200 text-xs">Ready to trade</div>
          <div className="absolute top-2 right-2 w-8 h-8 bg-white/10 rounded-full blur-xl"></div>
        </div>

        <div className={`p-6 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 text-white border ${
          totalPnL >= 0 
            ? 'bg-gradient-to-br from-green-400 via-green-500 to-emerald-600 shadow-green-500/30 hover:shadow-green-500/50 hover:shadow-xl border-green-300/20' 
            : 'bg-gradient-to-br from-red-400 via-red-500 to-rose-600 shadow-red-500/30 hover:shadow-red-500/50 hover:shadow-xl border-red-300/20'
        } relative overflow-hidden`}>
          <h3 className={`text-sm font-medium mb-2 ${
            totalPnL >= 0 ? 'text-green-100' : 'text-red-100'
          }`}>Today's P&L</h3>
          <p className="text-2xl font-bold text-white drop-shadow-lg">
            ₹{totalPnL.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </p>
          <div className={`mt-2 text-xs ${
            totalPnL >= 0 ? 'text-green-200' : 'text-red-200'
          }`}>
            {totalPnL >= 0 ? '📈 Profit' : '📉 Loss'}
          </div>
          <div className="absolute top-2 right-2 w-8 h-8 bg-white/10 rounded-full blur-xl"></div>
        </div>

        <div className="bg-gradient-to-br from-purple-400 via-purple-500 to-violet-600 text-white p-6 rounded-xl shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-purple-300/20 relative overflow-hidden">
          <h3 className="text-sm font-medium text-purple-100 mb-2">Open Positions</h3>
          <p className="text-2xl font-bold text-white drop-shadow-lg">{positions?.length || 0}</p>
          <div className="mt-2 text-purple-200 text-xs">Active trades</div>
          <div className="absolute top-2 right-2 w-8 h-8 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute -bottom-2 -right-2 w-16 h-16 bg-white/5 rounded-full blur-lg"></div>
        </div>

        <div className="bg-gradient-to-br from-orange-400 via-amber-500 to-yellow-500 text-white p-6 rounded-xl shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-orange-300/20 relative overflow-hidden">
          <h3 className="text-sm font-medium text-orange-100 mb-2">Pending Orders</h3>
          <p className="text-2xl font-bold text-white drop-shadow-lg">{pendingOrders}</p>
          <div className="mt-2 text-orange-200 text-xs">Awaiting execution</div>
          <div className="absolute top-2 right-2 w-8 h-8 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute -top-4 -left-4 w-12 h-12 bg-white/5 rounded-full blur-lg"></div>
          <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-white/5 rounded-full blur-md"></div>
        </div>
      </div>

      {/* Recent Positions */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Open Positions</h2>
        {positions && positions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table-auto w-full">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Qty</th>
                  <th>Avg Price</th>
                  <th>LTP</th>
                  <th>P&L</th>
                </tr>
              </thead>
              <tbody>
                {positions.slice(0, 10).map((pos, idx) => (
                  <tr key={idx}>
                    <td className="font-medium">{pos.tradingSymbol}</td>
                    <td>{pos.netQuantity}</td>
                    <td>₹{(Number(pos.netAveragePrice) || 0).toFixed(2)}</td>
                    <td>₹{(Number(pos.buyPrice) || 0).toFixed(2)}</td>
                    <td className={(Number(pos.realizedPnl) || 0) >= 0 ? 'text-success' : 'text-danger'}>
                      ₹{(Number(pos.realizedPnl) || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No open positions</p>
        )}
      </div>

      {/* Recent Orders */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Orders</h2>
        {orderBook.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table-auto w-full">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Type</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orderBook.slice(0, 10).map((order, idx) => (
                  <tr key={idx}>
                    <td className="font-medium">{order.tradingSymbol}</td>
                    <td>
                      <span className={order.transactionType === 'BUY' ? 'text-success' : 'text-danger'}>
                        {order.transactionType}
                      </span>
                    </td>
                    <td>{order.quantity}</td>
                    <td>₹{order.price}</td>
                    <td>
                      <span className={`px-2 py-1 rounded text-xs ${
                        order.orderStatus === 'COMPLETE' ? 'bg-success text-white' :
                        order.orderStatus === 'REJECTED' ? 'bg-danger text-white' :
                        'bg-gray-300 text-gray-800'
                      }`}>
                        {order.orderStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No recent orders</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
